import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Map } from 'immutable'

import { BookOutlined, CarOutlined, CopyOutlined, EditTwoTone, MessageOutlined, FlagOutlined, TeamOutlined, ShopOutlined } from '@ant-design/icons'
import { Alert, Badge, Button, message, Modal, Typography, Space } from 'antd'
import { CopyToClipboard } from 'react-copy-to-clipboard'

import SupplierNotesContainer from '../../SupplierNotes/SupplierNotesContainer'
import OrderNotesContainer from '../../OrderNotes/OrderNotesContainer'
import NoteForm from './NoteForm'
import CompanyProjects from './CompanyProjects'
import OrderStaffPills from '../../../../components/OrderStaffs/OrderStaffPills'
import ShippingDetail from './ShippingDetail'

import OrderSummaryDetailWrapper from './OrderSummaryDetailWrapper.style'

import inquiryActions from '../../../../redux/inquiry/actions'
import ShippingAddressFormRender from './ShippingAddresses/FormRender'
import SelectOldShippingAddressForm from './ShippingAddresses/SelectOldShippingAddressForm'
import SalePipelineInfo from './SalePipelineInfo'
import ScmFinishedOrder from '../../../../components/ScmFinishedOrder'

const { fetchInquiry, setShowSupplierNotesForm, setShowOrderNotesForm } = inquiryActions

const renderSuppliers = (orderEntity, supplierOrderEntities) => {
  return orderEntity.get('supplier_orders').map((supplierOrder) => {
    const supplierOrderEntity = supplierOrderEntities.get(supplierOrder)
    if (!supplierOrderEntity) return null
    return (
      <div key={supplierOrder}>
        {supplierOrderEntity.get('supplier_name')} ({supplierOrderEntity.get('state_label')}) {supplierOrderEntity.getIn(['grand_total', 'display'])}
      </div>
    )
  })
}

export const OrderSummaryLeft = ({ orderEntity }) => {
  const { i18n } = useTranslation(['inquiries', 'addresses'])

  const dispatch = useDispatch()
  const showOrderNotesForm = useSelector(state => state.getIn(['inquiry', 'showOrderNotesForm']))

  const [showNoteForm, setShowNoteForm] = useState(false)

  const handleOrderNotesForm = () => {
    dispatch(setShowOrderNotesForm(!showOrderNotesForm))
  }

  const handleNoteForm = () => {
    setShowNoteForm(!showNoteForm)
  }

  const onSaveSuccess = (number) => {
    dispatch(fetchInquiry(number))
  }

  const orderState = orderEntity.get('state')
  const receipt = orderEntity.get('sum_of_receipt') / 100.00
  const grandTotal = orderEntity.get('grand_total')
  const showReceiptWarning = receipt < grandTotal &&
    (orderState === 'payment' || orderState === 'complete')

  const renderNoteSection = () => {
    if (showNoteForm) {
      return (
        <NoteForm defaultNote={orderEntity.get('note')}
          orderId={orderEntity.get('id')}
          onCancel={handleNoteForm}
          onSuccess={handleNoteForm} />
      )
    }
    return (
      <div>
        <span style={{ marginRight: '8px' }}>{orderEntity.get('note') || '-'}</span>
        <EditTwoTone onClick={handleNoteForm} />
      </div>
    )
  }

  return (
    <OrderSummaryDetailWrapper>
      <div className='summary-row inline'>
        <BookOutlined className='summary-icon' />
        <span className='summary-label-inline'>โน้ต:</span>
        <div className='summary-content'>{renderNoteSection()}</div>
      </div>

      <div className='summary-row inline'>
        <FlagOutlined className='summary-icon' />
        <span className='summary-label-inline'>{i18n.t('orders:leadSources')}:</span>
        <div className='summary-content'>
          <Typography.Text>{orderEntity.get('lead_source') || '-'}</Typography.Text>
        </div>
      </div>

      <div className='summary-row inline quick-actions'>
        <Space wrap size={[16, 8]} align='center'>
          <CompanyProjects orderId={orderEntity.get('id')} />
          <SalePipelineInfo orderId={orderEntity.get('id')} />
          <Badge size="default" count={orderEntity.get('customer_notes_count')} offset={[7, -3]}>
            <MessageOutlined /> <a onClick={handleOrderNotesForm}>คุยกับลูกค้า</a>
          </Badge>
        </Space>
      </div>

      <div className='summary-row inline'>
        <TeamOutlined className='summary-icon' />
        <span className='summary-label-inline'>ผู้ดูแล:</span>
        <div className='summary-content'>
          <OrderStaffPills
            order={orderEntity.toJS()}
            onSaveSuccess={onSaveSuccess}
          />
        </div>
      </div>

      {showReceiptWarning &&
        <Alert
          type='error'
          showIcon
          style={{ marginTop: 8 }}
          message='เงินรับน้อยกว่ายอดเสนอ ห้ามปล่อยของ'
        />
      }

      <Modal open={showOrderNotesForm}
        footer={null}
        width={'50%'}
        onCancel={handleOrderNotesForm}>
        <OrderNotesContainer
          customerNo={orderEntity.getIn(['customer', 'id'])}
          orderId={orderEntity.get('id')} />
      </Modal>
    </OrderSummaryDetailWrapper>
  )
}

export const OrderSummaryRight = ({ orderEntity, supplierOrderEntities }) => {
  const { i18n } = useTranslation(['inquiries', 'addresses'])

  const dispatch = useDispatch()
  const showSupplierNotesForm = useSelector(state => state.getIn(['inquiry', 'showSupplierNotesForm']))
  const inquiry = useSelector(state => state.get('inquiry'))
  const customerEntities = useSelector(state => state.getIn(['Entities', 'customers']))

  const [showShippingAddressForm, setShowShippingAddressForm] = useState(false)
  const [showSelectShippingAddressForm, setShowSelectShippingAddressModal] = useState(false)

  const handleSupplierNotesForm = () => {
    dispatch(setShowSupplierNotesForm(!showSupplierNotesForm))
  }

  const handleShippingAddressForm = () => {
    setShowShippingAddressForm(!showShippingAddressForm)
  }

  const handleSelectShippingAddressForm = () => {
    setShowSelectShippingAddressModal(!showSelectShippingAddressForm)
  }

  const onSaveSuccess = (number) => {
    dispatch(fetchInquiry(number))
  }

  const customer = customerEntities.getIn([inquiry.get('currentCustomer')]) || new Map()
  const orderShippingAddress = orderEntity.get('order_shipping_address')
  const orderShippingAddressId = orderShippingAddress ? orderShippingAddress.get('id') : null

  const buildShippingClipboardText = () => {
    const lines = ['จัดส่ง']
    const shippingType = orderEntity.get('shipping_type')

    if (shippingType === 'self_pickup') {
      const customerName = `${customer.get('first_name') || ''} ${customer.get('last_name') || ''}`.trim()
      const branchName = orderEntity.getIn(['mh_branch', 'name'])
      if (branchName) lines.push(branchName)
      if (customerName) lines.push(customerName)
      const phone = customer.get('phone_number')
      if (phone) lines.push(phone)
    } else if (shippingType === 'not_specified_address') {
      lines.push(i18n.t('inquiries:order.unspecifiedAddress'))
    } else if (shippingType === 'export') {
      lines.push('ส่งออก')
      const country = orderEntity.getIn(['order_export_shipment', 'country_name'])
      if (country) lines.push(`ประเทศ: ${country}`)
    } else if (orderShippingAddress) {
      const customerName = `${customer.get('first_name') || ''} ${customer.get('last_name') || ''}`.trim()
      const detail = orderShippingAddress.get('shipping_detail')
      if (detail) lines.push(detail)
      const district = orderShippingAddress.getIn(['district', 'name'])
      const province = orderShippingAddress.getIn(['province', 'name'])
      const postal = orderShippingAddress.get('postal_code')
      lines.push(`${district || ''} ${province || ''}, ${postal || ''}`.trim())
      const additional = orderShippingAddress.get('additional_information')
      if (additional) lines.push(additional)
      const linkUrl = orderShippingAddress.get('link_url')
      if (linkUrl) lines.push(linkUrl)
      lines.push(orderShippingAddress.get('contact_name') || customerName)
      const phone = orderShippingAddress.get('phone_number')
      if (phone) lines.push(phone)
    }

    return lines.join('\n')
  }

  return (
    <OrderSummaryDetailWrapper>
      <div className='summary-row'>
        <CarOutlined className='summary-icon' />
        <div className='summary-content'>
          <div className='summary-label'>
            จัดส่ง <EditTwoTone onClick={handleShippingAddressForm} />
            <CopyToClipboard text={buildShippingClipboardText()} onCopy={() => message.success('Copied')}>
              <Button type='text' size='small' icon={<CopyOutlined />} style={{ marginLeft: 4 }} />
            </CopyToClipboard>
            <a onClick={handleSelectShippingAddressForm} style={{ marginLeft: 12, fontWeight: 'normal', fontSize: 12 }}>
              {i18n.t('addresses:selectOriginalAddress')}
            </a>
          </div>
          <ShippingDetail shippingType={orderEntity.get('shipping_type')}
            order={orderEntity}
            customer={customer}/>
        </div>
      </div>

      {orderEntity.get('supplier_orders') && orderEntity.get('supplier_orders').size > 0 && (
        <div className='summary-row inline'>
          <ShopOutlined className='summary-icon' />
          <span className='summary-label-inline'>Suppliers:</span>
          <div className='summary-content'>
            {renderSuppliers(orderEntity, supplierOrderEntities)}
          </div>
        </div>
      )}

      <div className='summary-row inline'>
        <div className='summary-content'>
          <Space wrap size={[16, 4]} align='center'>
            <Badge size="default" count={orderEntity.get('order_supplier_notes_count')} offset={[7, -3]}>
              <MessageOutlined /> <a onClick={handleSupplierNotesForm}>คุยกับ supplier</a>
            </Badge>
            <ScmFinishedOrder
              onSaveSuccess={onSaveSuccess}
              order={orderEntity.toJS()}
            />
          </Space>
        </div>
      </div>

      <Modal
        title={i18n.t('addresses:selectShippingAddress')}
        destroyOnClose
        onCancel={handleSelectShippingAddressForm}
        open={showSelectShippingAddressForm}
        footer={null}
        width="60%">
        <SelectOldShippingAddressForm onCancel={handleSelectShippingAddressForm}
          orderId={orderEntity.get('id')}
          customerId={orderEntity.getIn(['customer', 'id'])}
          orderShippingAddressId={orderShippingAddressId} />
      </Modal>
      <Modal open={showShippingAddressForm}
        footer={null}
        width={'50%'}
        onCancel={handleShippingAddressForm}>
        <ShippingAddressFormRender shippingAddress={orderEntity.get('order_shipping_address')}
          onCancel={handleShippingAddressForm}
          onSuccess={handleShippingAddressForm}
          defaultShippingType={orderEntity.get('shipping_type')}
          orderId={orderEntity.get('id')}
          exportShipment={orderEntity.get('order_export_shipment')}
          pickupLicensePlateNumber={orderEntity.get('pickup_license_plate_number')}
          pickupDate={orderEntity.get('pickup_date')} />
      </Modal>
      <Modal open={showSupplierNotesForm}
        footer={null}
        width={'50%'}
        onCancel={handleSupplierNotesForm}>
        <SupplierNotesContainer
          orderId={orderEntity.get('id')}/>
      </Modal>
    </OrderSummaryDetailWrapper>
  )
}

const OrderSummaryDetail = ({ orderEntity, supplierOrderEntities }) => (
  <>
    <OrderSummaryLeft orderEntity={orderEntity} />
    <OrderSummaryRight orderEntity={orderEntity} supplierOrderEntities={supplierOrderEntities} />
  </>
)

export default OrderSummaryDetail
